import PanToolIcon from "@mui/icons-material/PanTool";
import { useMediaQuery } from "@mui/material";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { ProgressButton, mobileWidth, useToast } from "@qubit/autoparts";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "~/app/store";

import {
  selectHandRaisedForWorkstation,
  selectWorkstationStatus,
  selectGridSystemMode
} from "~/features/andon/andon.selectors";

import { selectPortOpenByIdMap } from "~/features/andon/andon.slice";
import { AndonStatus } from "~/features/andon/andon.types";
import { figureAndonColorFromStatus } from "~/features/andon/andon.utils";
import { useClientConfig } from "~/hooks/useClientConfig";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { selectThisWorkstation } from "~/redux/selectors/workstationsSelectors";
import { useToggleHandRaiseMutation } from "~/redux/warehouse/andon.hooks";

export const WorkstationInformation = () => {
  const { t } = useTranslation();
  const { errorToast } = useToast();
  const isMobile = useMediaQuery(mobileWidth);

  const { an_andonFeaturesEnabled } = useClientConfig();
  const siteWorkstation = useAppSelector(selectThisWorkstation);
  const selectedAutostoreGridId = siteWorkstation?.autostoreGridId;

  const handRaised = useAppSelector(
    siteWorkstation
      ? selectHandRaisedForWorkstation(siteWorkstation.id)
      : () => undefined
  );
  const workstationStatus = useAppSelector(
    siteWorkstation
      ? selectWorkstationStatus(siteWorkstation.id)
      : () => "good" as AndonStatus
  );
  const portOpenById = useAppSelector(selectPortOpenByIdMap);
  const isOpen =
    siteWorkstation?.ports?.some((p) => portOpenById[p.portId.toString()]) ??
    false;
  const gridSystemMode = useAppSelector(
    siteWorkstation
      ? selectGridSystemMode(siteWorkstation.autostoreGridId)
      : () => null
  );

  const [toggleHandRaise, { isLoading }] = useToggleHandRaiseMutation();

  const calculatedColors = an_andonFeaturesEnabled
    ? figureAndonColorFromStatus({
        handRaised: handRaised?.raised ?? false,
        workstationOpen: isOpen,
        workstationStatus,
        workstationActive: siteWorkstation?.status === "Active",
        gridSystemMode: gridSystemMode ?? undefined
      })
    : undefined;

  const handleRaiseHandClick = async () => {
    if (!selectedAutostoreGridId || !siteWorkstation) {
      errorToast("Missing workstation information");
      return;
    }

    try {
      await toggleHandRaise({
        autostoreGridId: selectedAutostoreGridId,
        workstationId: siteWorkstation.id,
        raiseHand: !handRaised?.raised
      });
    } catch (error) {
      errorToast(getMessageFromRtkError(error));
    }
  };

  const shouldShowHandButton =
    an_andonFeaturesEnabled && !isMobile && siteWorkstation;
  const shouldShowWorkstationName = siteWorkstation && !isMobile;

  if (!shouldShowHandButton && !shouldShowWorkstationName) {
    return null;
  }

  return (
    <>
      {shouldShowHandButton && (
        <ProgressButton
          emphasis="high"
          responsive
          variant="contained"
          color={handRaised?.raised ? "info" : "inherit"}
          startIcon={
            <PanToolIcon
              sx={{
                color: "warning.dark"
              }}
            />
          }
          loading={isLoading}
          onClick={handleRaiseHandClick}
        >
          <Typography sx={{ minWidth: 100 }}>
            {handRaised?.raised ? t("lower hand") : t("raise hand")}
          </Typography>
        </ProgressButton>
      )}
      {shouldShowWorkstationName && (
        <Box
          role="region"
          sx={{
            display: "flex",
            justifyContent: "center",
            backgroundColor: calculatedColors?.backgroundColor,
            borderRadius: 1
          }}
        >
          <Typography
            sx={{
              margin: "5px",
              color: calculatedColors?.textColor
            }}
          >
            {t("nav.viewname.workstation")}: {siteWorkstation.deviceId}
          </Typography>
        </Box>
      )}
    </>
  );
};
